import React from 'react';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import useFeaturePermission from 'src/hooks/useFeaturePermission';
import { callApi } from 'src/utils/apiCall';
import { getLeadNotes } from 'src/redux/slices/lead';
import { useDispatch, useSelector } from 'src/redux/store';
import {
    MAX_POLL_ATTEMPTS,
    POLL_INTERVAL_MS,
    buildEndpoint,
    getRecordingMimeType,
} from './consultationRecordingsUtils';

export default function useConsultationRecordings(leadDetail) {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { canView, canManage } = useFeaturePermission('clinic-consultation-recording');
    const recorderRef = React.useRef(null);
    const streamRef = React.useRef(null);
    const chunksRef = React.useRef([]);
    const chunkUploadPromisesRef = React.useRef([]);
    const chunkSequenceRef = React.useRef(0);
    const timerRef = React.useRef(null);
    const startedAtRef = React.useRef(null);
    const recordingIdRef = React.useRef(null);
    const completeRecordingUploadRef = React.useRef(null);
    const isMountedRef = React.useRef(true);
    const mimeTypeRef = React.useRef('');
    const [recordings, setRecordings] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [recordingState, setRecordingState] = React.useState('idle');
    const [elapsedMs, setElapsedMs] = React.useState(0);
    const [error, setError] = React.useState('');

    const leadId = leadDetail?.id;
    const hasRecorder = typeof window !== 'undefined' && Boolean(
        window.MediaRecorder && window.navigator?.mediaDevices?.getUserMedia
    );

    const fetchRecordings = React.useCallback(async () => {
        if (!practiceId || !leadId || !canView) return [];
        setLoading(true);
        try {
            const response = await callApi(buildEndpoint(practiceId, leadId), 'GET', null, false, { bustCache: true });
            if (!isMountedRef.current) return [];
            if (response.status === 200) {
                const rows = Array.isArray(response.data) ? response.data : response.data?.results || [];
                setRecordings(rows);
                return rows;
            }
            setError(response?.data?.detail || response?.data?.error || 'Could not load consultation recordings.');
            return [];
        } catch (err) {
            if (isMountedRef.current) {
                setError(err?.message || 'Could not load consultation recordings.');
            }
            return [];
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [canView, leadId, practiceId]);

    React.useEffect(() => {
        fetchRecordings();
    }, [fetchRecordings]);

    const pollRecording = React.useCallback(async (recordingId, processRetry = false) => {
        if (processRetry) {
            await callApi(buildEndpoint(practiceId, leadId, `${recordingId}/process/`), 'post');
        }
        for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt += 1) {
            if (!isMountedRef.current) return null;
            // eslint-disable-next-line no-await-in-loop
            const response = await callApi(
                buildEndpoint(practiceId, leadId, `${recordingId}/`),
                'GET',
                null,
                false,
                { bustCache: true }
            );
            if (!isMountedRef.current) return null;
            if (response.status === 200) {
                setRecordings((previous) => [
                    response.data,
                    ...previous.filter((item) => item.id !== response.data.id),
                ]);
                if (['ready', 'failed'].includes(response.data.status)) return response.data;
            }
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => window.setTimeout(resolve, POLL_INTERVAL_MS));
        }
        return null;
    }, [leadId, practiceId]);

    const uploadRecordingChunk = React.useCallback(async (recordingId, blob, sequence) => {
        if (!recordingId || !blob?.size) return null;
        const formData = new FormData();
        const durationMs = startedAtRef.current ? Date.now() - startedAtRef.current : 0;
        formData.append('audio', blob, `consultation-${recordingId}-${sequence}.webm`);
        formData.append('sequence', String(sequence));
        formData.append('duration_ms', String(durationMs));
        formData.append('mime_type', mimeTypeRef.current || blob.type || 'audio/webm');
        const response = await callApi(
            buildEndpoint(practiceId, leadId, `${recordingId}/chunks/`),
            'post',
            formData,
            false,
            { timeout: 30000 }
        );
        if (response.status >= 400 || response.status === 0) {
            throw new Error(response?.data?.detail || response?.data?.error || 'Could not upload recording chunk.');
        }
        return response;
    }, [leadId, practiceId]);

    const completeRecordingUpload = React.useCallback(async ({ updateState = true } = {}) => {
        const recordingId = recordingIdRef.current;
        if (!recordingId) return null;

        streamRef.current?.getTracks?.().forEach((track) => track.stop());

        const blob = new Blob(chunksRef.current, { type: mimeTypeRef.current || 'audio/webm' });
        const chunkResults = await Promise.all(chunkUploadPromisesRef.current);
        const hasChunkUploadFailure = chunkResults.some((result) => !result?.ok);
        const formData = new FormData();
        formData.append('duration_ms', String(Date.now() - startedAtRef.current));
        formData.append('mime_type', mimeTypeRef.current || blob.type || '');
        if (!chunkUploadPromisesRef.current.length || hasChunkUploadFailure) {
            formData.append('audio', blob, 'consultation.webm');
        }
        const response = await callApi(
            buildEndpoint(practiceId, leadId, `${recordingId}/complete/`),
            'post',
            formData,
            false,
            { timeout: 60000 }
        );

        if (updateState && isMountedRef.current) {
            setRecordingState('processing');
            if (response.status === 200) {
                setRecordings((previous) => [response.data, ...previous.filter((item) => item.id !== response.data.id)]);
                const processed = await pollRecording(response.data.id);
                if (processed?.status === 'ready') {
                    enqueueSnackbar('Consultation recording processed', { variant: 'success' });
                } else if (processed?.status === 'failed') {
                    enqueueSnackbar('Consultation recording processing failed', { variant: 'error' });
                } else {
                    enqueueSnackbar('Recording uploaded. Processing is still running.', { variant: 'info' });
                }
            } else {
                setError(response?.data?.detail || response?.data?.error || 'Could not complete recording.');
            }
            setRecordingState('idle');
        }

        recordingIdRef.current = null;
        chunksRef.current = [];
        chunkUploadPromisesRef.current = [];
        chunkSequenceRef.current = 0;
        return response;
    }, [enqueueSnackbar, leadId, pollRecording, practiceId]);

    React.useEffect(() => {
        completeRecordingUploadRef.current = completeRecordingUpload;
    }, [completeRecordingUpload]);

    React.useEffect(() => () => {
        isMountedRef.current = false;
        window.clearInterval(timerRef.current);
        if (recorderRef.current?.state === 'recording') {
            recorderRef.current.onstop = () => {
                completeRecordingUploadRef.current?.({ updateState: false });
            };
            recorderRef.current.stop();
        } else {
            streamRef.current?.getTracks?.().forEach((track) => track.stop());
        }
    }, []);

    const startRecording = React.useCallback(async () => {
        if (!hasRecorder || !canManage) return;
        setError('');
        let stream;
        try {
            stream = await window.navigator.mediaDevices.getUserMedia({ audio: true });
            const createResponse = await callApi(buildEndpoint(practiceId, leadId), 'post', { provider: 'browser' });
            if (createResponse.status !== 201) {
                stream.getTracks().forEach((track) => track.stop());
                throw new Error(createResponse?.data?.detail || createResponse?.data?.error || 'Could not start recording.');
            }
            if (!isMountedRef.current) {
                stream.getTracks().forEach((track) => track.stop());
                return;
            }
            const mimeType = getRecordingMimeType();
            const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            streamRef.current = stream;
            recorderRef.current = recorder;
            chunksRef.current = [];
            chunkUploadPromisesRef.current = [];
            chunkSequenceRef.current = 0;
            recordingIdRef.current = createResponse.data.id;
            mimeTypeRef.current = mimeType || recorder.mimeType || '';
            startedAtRef.current = Date.now();
            setRecordings((previous) => [createResponse.data, ...previous.filter((item) => item.id !== createResponse.data.id)]);
            setRecordingState('recording');
            setElapsedMs(0);
            timerRef.current = window.setInterval(() => {
                setElapsedMs(Date.now() - startedAtRef.current);
            }, 1000);
            recorder.ondataavailable = (event) => {
                if (!event.data?.size) return;
                chunksRef.current.push(event.data);
                const sequence = chunkSequenceRef.current;
                chunkSequenceRef.current += 1;
                chunkUploadPromisesRef.current.push(
                    uploadRecordingChunk(createResponse.data.id, event.data, sequence)
                        .then((response) => ({ ok: true, response }))
                        .catch((error) => ({ ok: false, error }))
                );
            };
            recorder.start(5000);
        } catch (err) {
            stream?.getTracks?.().forEach((track) => track.stop());
            if (isMountedRef.current) {
                setRecordingState('idle');
                setError(err?.message || 'Microphone access failed.');
            }
        }
    }, [canManage, hasRecorder, leadId, practiceId, uploadRecordingChunk]);

    const stopRecording = React.useCallback(async () => {
        if (!recorderRef.current || recordingState !== 'recording') return;
        setRecordingState('stopping');
        window.clearInterval(timerRef.current);
        const recorder = recorderRef.current;
        await new Promise((resolve) => {
            recorder.onstop = resolve;
            recorder.stop();
        });
        await completeRecordingUpload();
    }, [completeRecordingUpload, recordingState]);

    const approveNote = React.useCallback(async (recordingId, additionalNotes) => {
        const response = await callApi(
            buildEndpoint(practiceId, leadId, `${recordingId}/approve-note/`),
            'post',
            { additional_notes: additionalNotes }
        );
        if (response.status === 200) {
            setRecordings((previous) => [
                response.data.recording,
                ...previous.filter((item) => item.id !== response.data.recording.id),
            ]);
            dispatch(getLeadNotes(leadId, practiceId));
            enqueueSnackbar('Consultation note saved', { variant: 'success' });
        } else {
            enqueueSnackbar(response?.data?.error || 'Could not save note', { variant: 'error' });
        }
    }, [dispatch, enqueueSnackbar, leadId, practiceId]);

    const updateSpeakerMap = React.useCallback(async (recordingId, speakerMap) => {
        setRecordings((previous) => previous.map((item) => (
            item.id === recordingId ? { ...item, speaker_map: speakerMap } : item
        )));
        const response = await callApi(
            buildEndpoint(practiceId, leadId, `${recordingId}/speaker-map/`),
            'patch',
            { speaker_map: speakerMap }
        );
        if (response.status === 200) {
            setRecordings((previous) => previous.map((item) => (
                item.id === recordingId ? response.data : item
            )));
        } else {
            enqueueSnackbar(response?.data?.error || 'Could not update speaker labels', { variant: 'error' });
            fetchRecordings();
        }
    }, [enqueueSnackbar, fetchRecordings, leadId, practiceId]);

    return {
        canView,
        canManage,
        elapsedMs,
        error,
        hasRecorder,
        loading,
        recordings,
        recordingState,
        approveNote,
        pollRecording,
        startRecording,
        stopRecording,
        updateSpeakerMap,
    };
}
